/**
 * Analytics Page
 *
 * Analytics overview with:
 * - Stats Cards (Gesparte Zeit, Gesparte Kosten, Stundenlohn, Fokus auf das was zählt)
 * - Workflow Analyse with comparison diagrams
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { AnalyticsSummary, TimeframeType } from '../types/analytics';
import toast from 'react-hot-toast';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Clock, Coins, Wallet, BarChart3, X } from 'lucide-react';

/**
 * Smart Value Formatters
 * Dynamically format values based on their magnitude for better readability
 */

// Format currency (Euro) based on value magnitude
const formatCurrency = (value: number): string => {
  if (value === 0) return '€0';

  const absValue = Math.abs(value);

  if (absValue < 0.01) {
    // Very small values: show 3 decimals
    return `€${value.toFixed(3)}`;
  } else if (absValue < 1) {
    // Less than 1 Euro: show 2 decimals (cents)
    return `€${value.toFixed(2)}`;
  } else if (absValue < 10) {
    // Less than 10 Euro: show 1 decimal
    return `€${value.toFixed(1)}`;
  } else if (absValue < 1000) {
    // Less than 1000 Euro: show whole numbers
    return `€${Math.round(value)}`;
  } else {
    // Large values: use k suffix
    return `€${(value / 1000).toFixed(1)}k`;
  }
};

// Format time based on value magnitude (value is in hours)
const formatTime = (hours: number): string => {
  if (hours === 0) return '0min';

  const absHours = Math.abs(hours);

  if (absHours < 1/60) {
    // Less than 1 minute: show seconds
    const seconds = Math.round(hours * 3600);
    return `${seconds}s`;
  } else if (absHours < 1) {
    // Less than 1 hour: show minutes
    const minutes = Math.round(hours * 60);
    return `${minutes}min`;
  } else if (absHours < 10) {
    // Less than 10 hours: show 1 decimal
    return `${hours.toFixed(1)}h`;
  } else {
    // 10+ hours: show whole numbers
    return `${Math.round(hours)}h`;
  }
};

// Format executions (always whole numbers but with k suffix for large values)
const formatExecutions = (value: number): string => {
  if (value === 0) return '0';

  if (value < 1000) {
    return Math.round(value).toString();
  } else {
    return `${(value / 1000).toFixed(1)}k`;
  }
};

/**
 * Area Chart with Gradient Fill Component
 * Supports multiple data series with gradient fills
 */
interface ChartDataSeries {
  key: string;
  label: string;
  color: string;
}

interface AreaChartGradientProps {
  data: Array<Record<string, string | number>>;
  series: ChartDataSeries[];
  xAxisKey: string;
  height?: number;
  valueFormatter?: (value: number) => string;
}

const AreaChartGradient: React.FC<AreaChartGradientProps> = ({
  data,
  series,
  xAxisKey,
  height = 300,
  valueFormatter = (v) => v.toString(),
}) => {
  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
        >
          <defs>
            {series.map((s) => (
              <linearGradient key={`fill-${s.key}`} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={s.color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={s.color} stopOpacity={0.1} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2C2C2C" />
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fill: '#535353', fontSize: 12 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fill: '#535353', fontSize: 12 }}
            tickFormatter={(value) => valueFormatter(value)}
          />
          <Tooltip
            formatter={(value, name) => {
              const seriesItem = series.find((s) => s.key === name);
              const numValue = typeof value === 'number' ? value : 0;
              return [valueFormatter(numValue), seriesItem?.label || name];
            }}
            contentStyle={{
              backgroundColor: '#171717',
              border: '1px solid #2C2C2C',
              borderRadius: '8px',
              color: '#ffffff',
            }}
            labelStyle={{ color: '#ffffff' }}
          />
          <Legend
            formatter={(value) => {
              const seriesItem = series.find((s) => s.key === value);
              return seriesItem?.label || value;
            }}
          />
          {series.map((s) => (
            <Area
              key={s.key}
              dataKey={s.key}
              type="monotone"
              fill={`url(#fill-${s.key})`}
              fillOpacity={0.4}
              stroke={s.color}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

type ChartTab = 'comparison' | 'time' | 'cost' | 'executions';

export const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ChartTab>('comparison');
  const [timeframe, setTimeframe] = useState<TimeframeType>('week');
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<'cost' | 'time' | 'executions' | 'workflowCost'>('cost');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Fetch analytics data from API
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        console.log('[Analytics] Starting to fetch analytics data...', { timeframe });
        const data = await apiClient.getAnalytics(timeframe);
        console.log('[Analytics] Successfully fetched analytics:', data);
        setAnalytics(data);
      } catch (error) {
        console.error('[Analytics] Failed to load analytics:', error);
        if (error instanceof Error) {
          console.error('[Analytics] Error message:', error.message);
          console.error('[Analytics] Error stack:', error.stack);
        }
        toast.error('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    console.log('[Analytics] Fetching analytics with timeframe:', timeframe);
    fetchAnalytics();
  }, [timeframe]); // Re-fetch when timeframe changes

  // Calculate weighted average hourly rate based on executions
  let totalWeightedRate = 0;
  let totalExecutionsWithRate = 0;

  if (analytics) {
    analytics.workflows.forEach(w => {
      if (w.hourly_rate_euros !== null && w.execution_count > 0) {
        totalWeightedRate += w.hourly_rate_euros * w.execution_count;
        totalExecutionsWithRate += w.execution_count;
      }
    });
  }

  const weightedAverageRate = totalExecutionsWithRate > 0
    ? totalWeightedRate / totalExecutionsWithRate
    : 0;

  // Prepare chart data from time-series in recharts format
  const prepareChartData = () => {
    if (!analytics?.time_series?.length) {
      return { chartData: [], hasData: false };
    }

    // Create data array in recharts format
    const chartData = analytics.time_series.map((d) => {
      // Parse YYYY-MM-DD as local date (avoid UTC shift from new Date("YYYY-MM-DD"))
      const [yearStr, monthStr, dayStr] = d.date.split('-');
      const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
      let label: string;
      if (analytics.timeframe === 'week' || analytics.timeframe === 'month') {
        label = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      } else if (analytics.timeframe === 'year') {
        label = date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
      } else {
        label = date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
      }

      const timeSavedHours = d.cumulative_time_saved_minutes / 60;

      // Net cost savings = human cost savings minus workflow execution costs
      const netCostSaved = d.cumulative_cost_saved_euros - d.cumulative_workflow_cost_euros;

      return {
        label,
        time: Math.round(timeSavedHours * 10) / 10, // Round to 1 decimal
        cost: Math.round(netCostSaved * 100) / 100,
        humanCost: Math.round(d.cumulative_human_cost_euros * 100) / 100,
        workflowCost: Math.round(d.cumulative_workflow_cost_euros * 100) / 100,
        executions: d.execution_count,
      };
    });

    return { chartData, hasData: true };
  };

  // Handle sort field and direction changes
  const handleSort = (field: 'cost' | 'time' | 'executions' | 'workflowCost') => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      // Set new field with default desc direction
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort workflows for the workflow list
  const getFilteredAndSortedWorkflows = () => {
    if (!analytics?.workflows) return [];

    // Filter: Only workflows with at least one execution
    let filtered = analytics.workflows.filter(w => w.execution_count > 0);

    // Search: Case-insensitive name matching
    if (searchQuery.trim()) {
      filtered = filtered.filter(w =>
        w.workflow_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: number, bValue: number;

      switch (sortField) {
        case 'cost':
          aValue = (a.cost_saved_euros || 0) - (a.cost_per_execution_euros || 0) * a.execution_count;
          bValue = (b.cost_saved_euros || 0) - (b.cost_per_execution_euros || 0) * b.execution_count;
          break;
        case 'time':
          aValue = a.total_time_saved_minutes || 0;
          bValue = b.total_time_saved_minutes || 0;
          break;
        case 'executions':
          aValue = a.execution_count;
          bValue = b.execution_count;
          break;
        case 'workflowCost':
          aValue = (a.cost_per_execution_euros || 0) * a.execution_count;
          bValue = (b.cost_per_execution_euros || 0) * b.execution_count;
          break;
      }

      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    });

    return filtered;
  };

  // Helper function to get timeframe label
  const getTimeframeLabel = (timeframe: TimeframeType): string => {
    switch (timeframe) {
      case 'week':
        return 'diese Woche';
      case 'month':
        return 'diesen Monat';
      case 'year':
        return 'dieses Jahr';
      case 'all':
        return 'gesamt';
    }
  };

  // Get the total for the current timeframe (last value in time series)
  // Returns raw values without rounding - formatters will handle display
  const getTimeframeTotals = () => {
    if (!analytics?.time_series?.length) {
      return { time: 0, cost: 0, humanCost: 0, workflowCost: 0, executions: 0 };
    }

    const lastDataPoint = analytics.time_series[analytics.time_series.length - 1];
    return {
      time: lastDataPoint.cumulative_time_saved_minutes / 60, // Convert to hours (no rounding)
      cost: lastDataPoint.cumulative_cost_saved_euros - lastDataPoint.cumulative_workflow_cost_euros, // Net savings
      humanCost: lastDataPoint.cumulative_human_cost_euros,
      workflowCost: lastDataPoint.cumulative_workflow_cost_euros,
      executions: lastDataPoint.execution_count
    };
  };

  const { chartData, hasData } = prepareChartData();
  const timeframeTotals = getTimeframeTotals();
  const filteredWorkflows = getFilteredAndSortedWorkflows();

  // Loading state
  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-lg text-muted-foreground">Loading analytics...</div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!analytics) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-lg text-muted-foreground">No analytics data available</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Übersicht über Ihre Workflow-Analysen
          </p>
        </div>

        {/* Timeframe Selector for Stats */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-foreground">
            Ersparnisse {getTimeframeLabel(timeframe)}
          </h2>
          <div className="flex gap-2">
            {(['week', 'month', 'year', 'all'] as TimeframeType[]).map((tf) => (
              <Button
                key={tf}
                variant={timeframe === tf ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeframe(tf)}
              >
                {tf === 'week' ? 'Woche' : tf === 'month' ? 'Monat' : tf === 'year' ? 'Jahr' : 'Gesamt'}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats Cards - Use timeframe-filtered data to match charts */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Gesparte Zeit</p>
                  <p className="text-2xl font-semibold text-foreground">
                    {timeframeTotals.time.toFixed(1)}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Coins className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Gesparte Kosten</p>
                  <p className="text-2xl font-semibold text-foreground">
                    €{timeframeTotals.cost.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-secondary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Durchschn. Stundenlohn</p>
                  <p className="text-2xl font-semibold text-foreground">€{weightedAverageRate.toFixed(2)}/h</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-secondary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Durchschn. Zeitersparnis</p>
                  <p className="text-2xl font-semibold text-foreground">{analytics.average_time_saved_per_workflow_hours}h/Workflow</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Workflow Analyse */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-foreground">
                Workflow Analyse
              </h3>
            </div>

            {/* Tabs with Timeframe Summary */}
            <div className="border-b border-border mb-6">
              <div className="flex justify-between items-center">
                {/* Left: Tabs */}
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {([
                    { key: 'comparison', label: 'Kostenvergleich' },
                    { key: 'time', label: 'Gesparte Zeit' },
                    { key: 'cost', label: 'Gesparte Kosten' },
                    { key: 'executions', label: 'Ausführungen' },
                  ] as { key: ChartTab; label: string }[]).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>

                {/* Right: Timeframe Summary Box */}
                {hasData && (
                  <div className="px-4 py-2 rounded-lg bg-muted border border-border">
                    <div className="text-sm font-medium text-muted-foreground">
                      {activeTab === 'comparison' ? 'Ersparnis' :
                       activeTab === 'time' ? 'Gesparte Zeit' :
                       activeTab === 'cost' ? 'Gesparte Kosten' :
                       'Ausführungen'} {getTimeframeLabel(timeframe)}:
                      <span className="ml-2 font-bold text-primary">
                        {activeTab === 'comparison' ? formatCurrency(timeframeTotals.humanCost - timeframeTotals.workflowCost) :
                         activeTab === 'time' ? formatTime(timeframeTotals.time) :
                         activeTab === 'cost' ? formatCurrency(timeframeTotals.cost) :
                         formatExecutions(timeframeTotals.executions)
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chart Content */}
            <div className="mt-6 w-full">
              {!hasData ? (
                <div className="text-center py-12 text-muted-foreground">
                  Keine Daten für den ausgewählten Zeitraum verfügbar
                </div>
              ) : (
                <>
                  {activeTab === 'comparison' && (
                    <AreaChartGradient
                      data={chartData}
                      series={[
                        { key: 'humanCost', label: 'Menschliche Kosten', color: '#FCD19B' },
                        { key: 'workflowCost', label: 'Workflow-Kosten', color: '#6EFED4' },
                      ]}
                      xAxisKey="label"
                      height={300}
                      valueFormatter={formatCurrency}
                    />
                  )}

                  {activeTab === 'time' && (
                    <AreaChartGradient
                      data={chartData}
                      series={[
                        { key: 'time', label: 'Gesparte Zeit (kumulativ)', color: '#6EFED4' },
                      ]}
                      xAxisKey="label"
                      height={300}
                      valueFormatter={formatTime}
                    />
                  )}

                  {activeTab === 'cost' && (
                    <AreaChartGradient
                      data={chartData}
                      series={[
                        { key: 'cost', label: 'Gesparte Kosten (kumulativ)', color: '#6EFED4' },
                      ]}
                      xAxisKey="label"
                      height={300}
                      valueFormatter={formatCurrency}
                    />
                  )}

                  {activeTab === 'executions' && (
                    <AreaChartGradient
                      data={chartData}
                      series={[
                        { key: 'executions', label: 'Ausführungen (kumulativ)', color: '#FCD19B' },
                      ]}
                      xAxisKey="label"
                      height={300}
                      valueFormatter={formatExecutions}
                    />
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Workflow Performance Liste */}
        <Card className="mt-8">
          <CardContent className="p-6">
            {/* Header mit Titel und Suchfeld */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-foreground">
                Workflow Performance
              </h3>

              {/* Suchfeld */}
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Workflow suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Tabelle */}
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Workflow Name
                    </th>
                    <th
                      onClick={() => handleSort('executions')}
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                    >
                      Ausführungen {sortField === 'executions' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      onClick={() => handleSort('time')}
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                    >
                      Gesparte Zeit {sortField === 'time' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      onClick={() => handleSort('cost')}
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                    >
                      Gesparte Kosten {sortField === 'cost' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      onClick={() => handleSort('workflowCost')}
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                    >
                      Workflow-Kosten {sortField === 'workflowCost' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredWorkflows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                        {searchQuery
                          ? `Keine Workflows gefunden für "${searchQuery}"`
                          : 'Keine ausgeführten Workflows vorhanden'
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredWorkflows.map((workflow) => (
                      <tr key={workflow.workflow_id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-foreground">
                            {workflow.workflow_name}
                          </div>
                          {workflow.tags.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {workflow.tags.map((tag, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {workflow.execution_count}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {workflow.total_time_saved_minutes !== null
                            ? `${Math.round(workflow.total_time_saved_minutes / 60)}h`
                            : '-'
                          }
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {workflow.cost_saved_euros !== null
                            ? (() => {
                                const workflowCosts = (workflow.cost_per_execution_euros || 0) * workflow.execution_count;
                                const netSavings = workflow.cost_saved_euros - workflowCosts;
                                return `€${netSavings.toLocaleString('de-DE', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}`;
                              })()
                            : '-'
                          }
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {workflow.cost_per_execution_euros !== null
                            ? `€${(workflow.cost_per_execution_euros * workflow.execution_count).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}`
                            : '-'
                          }
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

